import { forwardRef } from "react";
import type { LegacyRef, ButtonHTMLAttributes, FC } from "react";
import type { IconSvgElement } from "@hugeicons/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { cx } from "../../utils/classnames";

export enum IconButtonVariant {
  PRIMARY = "PRIMARY",
  BASE = "BASE",
  TEXT = "TEXT"
}

export enum IconButtonSize {
  SM = "SM",
  BASE = "BASE",
  LG = "LG"
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- we will change Icon props a lot
type UntitledIcon = FC<any>;
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  Icon?: UntitledIcon;
  HugeIcon?: IconSvgElement;
  type?: "submit" | "reset" | "button";
  loading?: boolean;
  borderLess?: boolean;
  disabled?: boolean;
}

const IconBridge = ({
  HugeIcon,
  size,
  Icon
}: {
  HugeIcon?: IconSvgElement;
  size: IconButtonSize;
  Icon?: UntitledIcon;
}) => {
  if (HugeIcon) {
    return (
      <HugeiconsIcon
        icon={HugeIcon}
        size={size === IconButtonSize.LG ? "24" : "20"}
        stroke="inherit"
        className="mx-auto"
      />
    );
  }
  if (Icon)
    return (
      <Icon
        className="mx-auto"
        size={size === IconButtonSize.LG ? "24" : "20"}
        stroke="inherit"
      />
    );

  return null;
};

const buttonVariant = {
  [IconButtonVariant.PRIMARY]:
    "bg-primary-500 text-white stroke-white border-transparent text-white hover:bg-primary-600",
  // TODO: Most probably we will not need this variant
  // [ButtonVariant.DANGER]: "bg-content-1000-inverted border text-danger-500 border-danger-500 hover:bg-danger-50",
  [IconButtonVariant.BASE]:
    "bg-content-1000-inverted text-content-1000 stroke-content-1000 border-alpha-100 hover:bg-alpha-40 disabled:hover:bg-content-1000-inverted disabled:stroke-alpha-100 disabled:cursor-not-allowed",
  [IconButtonVariant.TEXT]:
    "text-primary-500 stroke-primary-500 hover:text-primary-600 hover:stroke-primary-600"
};

const buttonSize = (isInline: boolean) => {
  return {
    [IconButtonSize.SM]: cx(
      "text-sm",
      !isInline ? "min-h-[40px] h-[40px] max-h-[40px] w-[40px] rounded-lg" : ""
    ),
    [IconButtonSize.BASE]: cx(
      "text-sm",
      !isInline ? "min-h-[48px] h-[48px] max-h-[48px] w-[48px] rounded-lg" : ""
    ),
    [IconButtonSize.LG]: cx(
      "text-base",
      !isInline ? "min-h-[56px] h-[56px] max-h-[56px] w-[56px] rounded-lg" : ""
    )
  };
};

export const IconButtonComp = (
  {
    className,
    loading,
    Icon,
    variant = IconButtonVariant.BASE,
    size = IconButtonSize.BASE,
    borderLess,
    type,
    HugeIcon,
    disabled,
    ...rest
  }: ButtonProps,
  ref?: LegacyRef<HTMLButtonElement>
) => {
  if (
    process.env.NODE_ENV !== "production" &&
    borderLess &&
    variant !== IconButtonVariant.BASE
  ) {
    throw new Error("Button with prop borderLess must have variant BASE");
  }

  const isInline = Boolean(
    variant === IconButtonVariant.TEXT || className?.includes("inline")
  );

  const baseClass = (isInline ? "inline" : "border-box").concat(
    " ",
    "stroke-2 font-bold transition-all outline-inherit focus:outline-1 focus:outline-primary-500"
  );

  return (
    <button
      className={cx(
        baseClass,
        buttonVariant[variant],
        buttonSize(isInline)[size],
        borderLess || variant === IconButtonVariant.TEXT
          ? "border-[none]"
          : "border shadow-xs",
        className
      )}
      disabled={disabled || loading}
      ref={ref}
      type={type || "button"}
      {...rest}
    >
      <IconBridge size={size} Icon={Icon} HugeIcon={HugeIcon} />
    </button>
  );
};

const IconButton = forwardRef(IconButtonComp);
IconButton.displayName;

export { IconButton };
